/**
 * ApiError — the ONE rejection type of the canonical client contract
 * (RES-CLIENT-001 §8.3).
 *
 * ## 🔴 Why a real `Error`, measured rather than argued
 *
 * `createApiClient`'s response interceptor builds an **object literal**
 * (`createApiClient.js:52-64`) and rejects it (`:311`). So in eight of ten web
 * apps every `catch (err)` receives a value for which `err instanceof Error` is
 * **false**, carrying no `stack`. Fleet-wide there are **68** `instanceof Error`
 * checks and **92** `.stack` reads in production web code, every one silently
 * taking the wrong branch for an API failure.
 *
 * The error path also carries the same provenance defect as the success path.
 * `err.response.data.error.code` is correct for exactly the two bespoke pos-web
 * clients (which register no response interceptor) and **inert for every other
 * client in the fleet** — **253** catch sites, all falling through to
 * `|| err.message`. Two are confirmed live defects: the IAR-001 elevated-auth
 * re-prompt never fires (`useDeviceManagement.js:128`), and pos-web writes **no**
 * `SERVER_ERROR` / `AUTH_ERROR` security-audit rows for any API failure
 * (`apiInterceptors.js:331-343`).
 *
 * `ApiError` fixes the class structurally: one rejection type, `instanceof`-
 * checkable, `status` / `errorCode` / `errorDetails` at the TOP level, and the
 * full body on `.body`. **`.response` is deliberately NOT added** — it never
 * existed on a normalized error, and re-creating it would make 253 broken reads
 * appear to work while still reading a body one level from where they think.
 * They are converted, not preserved.
 *
 * ## `code` vs `errorCode` — both load-bearing, meanings unchanged
 *
 *   - `code` is the AXIOS TRANSPORT code (`ERR_NETWORK`, `ERR_CANCELED`). ~262
 *     sites read it. Its meaning must never change.
 *   - `errorCode` is the SERVER's SCREAMING_SNAKE business code from the
 *     RES-ENV-001 envelope (`PERIOD_OVERLAP`, `BUDGET_ALREADY_EXISTS`).
 *
 * `status > 0` is what distinguishes "the server answered and refused" from
 * "the request never arrived".
 *
 * @module @puffin-core/api-client/facade/apiError
 */

import { readHeader } from '../htmlResponseGuard.js';

/**
 * Used when nothing better is available. Never an empty string: `new Error('')`
 * renders as nothing at all, which is the "409 error" class of unusable failure
 * report this contract exists to end.
 */
const DEFAULT_MESSAGE = 'API request failed';

/**
 * The canonical API failure.
 *
 * Field names match today's `NormalizedError` so the **77** genuinely-working
 * consumers of `err.errorCode` / `err.errorDetails` keep working verbatim. Two
 * deliberate differences: the value is a real `Error`, and the wire body moves
 * from `data` to **`body`** — because the token `data` carries no meaning
 * anywhere in this contract (§8.5), which is what lets the lint rule be an
 * absolute key ban with zero exemptions.
 */
export class ApiError extends Error {
  /**
   * @param {Object} [fields]
   * @param {number} [fields.status=0] - HTTP status; 0 when the request never completed.
   * @param {string} [fields.message] - The SERVER's sentence when it sent one, else axios's.
   * @param {string|null} [fields.code=null] - Axios transport code.
   * @param {string|null} [fields.errorCode=null] - Server business code.
   * @param {*|null} [fields.errorDetails=null] - Server `error.details`.
   * @param {string|null} [fields.requestId=null] - Correlation id, when the server sent one.
   * @param {*|null} [fields.body=null] - The full response body.
   */
  constructor({ status, message, code, errorCode, errorDetails, requestId, body } = {}) {
    super(message || DEFAULT_MESSAGE);

    this.name = 'ApiError';
    this.status = status ?? 0;
    this.code = code ?? null;
    this.errorCode = errorCode ?? null;
    this.errorDetails = errorDetails ?? null;
    this.requestId = requestId ?? null;
    this.body = body ?? null;

    // V8 captureStackTrace hides the constructor frame, matching the two sibling
    // error classes in this package.
    if (typeof Error.captureStackTrace === 'function') {
      Error.captureStackTrace(this, this.constructor);
    }
  }
}

// Anchor `name` on the prototype so `ApiError.prototype.name` answers before any
// instance exists — the convention `NoAuthTokenError` and `HtmlResponseError` set.
ApiError.prototype.name = 'ApiError';

/**
 * `Object.prototype.toString` tags that mean "this value is already a thrown error".
 *
 * 🔴 **`instanceof Error` alone is NOT sufficient, and this was MEASURED, not
 * assumed.** Under `jest-environment-node` the sandbox gets the HOST realm's
 * `DOMException`, whose prototype chain ends at the host's `Error.prototype`, so
 * `new DOMException('…', 'AbortError') instanceof Error` is **`false`** while
 * `Object.prototype.toString.call(…)` is `'[object DOMException]'` (probed in this
 * package's own jest config, 2026-08-20). A facade relying on `instanceof` alone
 * therefore converts every ABORT into an `ApiError` under test — and the fleet's
 * `err.name === 'AbortError'` filters, which exist so a cancelled request renders
 * NOTHING, would instead render an error state. Green suites asserting the wrong
 * behaviour is the exact "two green halves, never compared" failure this initiative
 * exists to end, so the discriminator is cross-realm-safe by construction.
 *
 * The tag is not duck-typing: `[object Error]` requires the [[ErrorData]] internal
 * slot and `[object DOMException]` requires DOMException's `Symbol.toStringTag`.
 * A `NormalizedError` plain object tags as `[object Object]` and is unaffected.
 */
const ERROR_VALUE_TAGS = new Set(['[object Error]', '[object DOMException]']);

/**
 * Is this rejected value already a proper error that must pass through untouched?
 *
 * @param {*} value - The rejected value.
 * @returns {boolean}
 */
function isErrorValue(value) {
  return value instanceof Error || ERROR_VALUE_TAGS.has(Object.prototype.toString.call(value));
}

/**
 * A message for a rejection that carried none.
 *
 * @param {*} cause - The raw rejected value.
 * @returns {string}
 */
function messageFromCause(cause) {
  return typeof cause === 'string' && cause.length > 0 ? cause : DEFAULT_MESSAGE;
}

/**
 * Convert whatever the transport rejected into the contract's rejection value.
 *
 * ## The single-provenance rule
 *
 * The facade wraps a `createApiClient` instance, and that instance rejects
 * exactly TWO kinds of value: a typed `Error` (`NoAuthTokenError`,
 * `HtmlResponseError`, an `AbortError` DOMException) or the `NormalizedError`
 * plain object. So this reader needs **one** rule for each, and no shape
 * detection at all — a converter that sniffed which of two transports produced
 * its input would be the very disease this initiative exists to cure.
 *
 * Anything already an error value passes through **untouched** (see
 * `isErrorValue` — `instanceof Error` alone is measurably not enough). That
 * covers the three typed errors the contract promises to keep typed, and it also
 * means a genuine programming error (a `TypeError` thrown inside a consumer's
 * `getToken`) is never disguised as `status: 0`, which would route a debuggable
 * bug to the "you are offline" branch.
 *
 * @param {*} cause - The rejected value.
 * @returns {Error} An `ApiError`, or `cause` itself when it is already an Error.
 */
export function toApiError(cause) {
  if (isErrorValue(cause)) {
    return cause;
  }

  const source = typeof cause === 'object' && cause !== null ? cause : {};
  const body = source.data ?? null;

  return new ApiError({
    status: source.status,
    message: source.message ?? messageFromCause(cause),
    code: source.code,
    errorCode: source.errorCode,
    errorDetails: source.errorDetails,
    // §8.3: `body.requestId ?? headers['x-request-id'] ?? null`. RES-ENV-001 puts
    // `requestId` TOP-LEVEL on an error envelope (`envelope.js:86`), outside
    // `error`, so the body is the primary source on this path. The header arm is
    // live the moment a transport supplies headers — see Q-N14-1.
    requestId: body?.requestId ?? readHeader(source.headers, 'x-request-id'),
    body,
  });
}
